import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, isDevMode, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DateTime } from 'luxon';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ClipboardService } from 'src/app/services/clipboard/clipboard.service';
import { CopilotFacadeService } from 'src/app/services/copilot/copilot-facade.service';
import { COPILOT_GUIDANCE_CAPABILITIES, CopilotGuidanceCapability } from 'src/app/services/copilot/copilot-guidance.constants';
import {
  COPILOT_AGENT_SKILLS_FLAG,
  COPILOT_EMPTY_STATE_SEEN_KEY,
  COPILOT_FEATURE_FLAG,
  COPILOT_FIRST_RUN_TIPS_KEY,
  COPILOT_MULTI_PERSONA_ENABLED,
  COPILOT_PANEL_DEFAULT_WIDTH,
  COPILOT_PANEL_MAX_WIDTH,
  COPILOT_PANEL_MIN_WIDTH,
  COPILOT_PANEL_WIDTH_STORAGE_KEY,
  COPILOT_PHASE1_AGENT_SKILLS,
  COPILOT_USAGE_TOP_USERS_FLAG,
  copilotHasRouteSpecificSuggestions,
  copilotSuggestionMeta,
  copilotSuggestionsForContext,
  resolveCopilotAgentSkills
} from 'src/app/services/copilot/copilot.constants';
import {
  buildCopilotSkillsList,
  type CopilotSkillListItem
} from 'src/app/services/copilot/copilot-skills.catalog';
import { CopilotAction, CopilotAgentSettings, CopilotClarification, CopilotConversationTab, CopilotLiveDataStatus, CopilotPanelScope, CopilotUiMessage, CoachingSuggestion, HelpLink, RecommendationAction, RecommendationCard, AdvisorPersonaId, TempoPersonalityId, TokenQuotaUsage, ReportLink, TableSpec, ToolStep } from 'src/app/services/copilot/copilot.types';
import { splitReportLinkPhrases, type ReportAnswerSegment } from 'src/app/services/copilot/split-report-link-phrases.util';
import {
  isSinglePersonCard,
  peopleTablesForMessage,
  shortFormatToggleFlags
} from 'src/app/services/copilot/short-format-visual-ui.util';
import type { CopilotDateClarificationPreset } from 'src/app/services/copilot/copilot-date-clarification.util';
import { resolveRecommendationDraftLabel } from 'src/app/services/copilot/copilot-recommendation-label.util';
import { DEFAULT_COPILOT_AGENT_SETTINGS } from 'src/app/services/copilot/copilot-agent-settings.util';
import { COPILOT_UNTITLED_CONVERSATION } from 'src/app/services/copilot/copilot-conversations.util';
import { AgentGatewayService } from 'src/app/services/copilot/agent-gateway.service';
import { normalizeInclusivePanelDateRange } from 'src/app/services/copilot/copilot-scope.util';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ResolveService } from 'src/app/services/resolve.service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { AuthState } from 'src/app/store/auth/auth.state';
import { ManagerState } from 'src/app/store/manager/manager.state';
import { Store } from '@ngxs/store';
import { MentionComposerComponent, MentionComposerSubmit, MentionComposerClarification } from 'src/app/components/mention-composer/mention-composer.component';
import { userMessageSegments, UserMessageDisplayPart } from 'src/app/components/mention-composer/mention-composer.util';
import { environment } from 'src/environments/environment';

type CopilotPanelView = 'chat' | 'guide' | 'usage' | 'skills';

export interface CopilotTopUserRow {
  userId: string;
  name: string;
  tokens: number;
  rank: number;
}

interface CopilotScopePreset {
  id: string;
  labelKey: string;
  range: () => { from: string, to: string };
}

const ADVISOR_PERSONA_OPTIONS: ReadonlyArray<{
  id: AdvisorPersonaId;
  labelKey: string;
  subtitleKey: string;
}> = [
  { id: 'kevin', labelKey: 'copilot.advisors.kevin.title', subtitleKey: 'copilot.advisors.kevin.subtitle' },
  { id: 'maya', labelKey: 'copilot.advisors.maya.title', subtitleKey: 'copilot.advisors.maya.subtitle' },
  { id: 'alex', labelKey: 'copilot.advisors.alex.title', subtitleKey: 'copilot.advisors.alex.subtitle' },
  { id: 'sam', labelKey: 'copilot.advisors.sam.title', subtitleKey: 'copilot.advisors.sam.subtitle' },
];

@Component({
  selector: 'app-copilot-sidepanel',
  templateUrl: './copilot-sidepanel.component.html',
  styleUrls: ['./copilot-sidepanel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class CopilotSidepanelComponent implements OnInit, AfterViewChecked, OnDestroy {
  readonly isDevMode = isDevMode();
  readonly assistantDisplayName = environment.assistantDisplayName;
  readonly isOpen$: Observable<boolean> = this.facade.isOpen$;
  readonly isSending$: Observable<boolean> = this.facade.isSending$;
  readonly messages$: Observable<CopilotUiMessage[]> = this.facade.messages$;
  readonly conversations$: Observable<CopilotConversationTab[]> = this.facade.conversations$;
  readonly activeConversationId$: Observable<string> = this.facade.activeConversationId$;
  readonly liveData$: Observable<CopilotLiveDataStatus> = this.facade.liveData$;
  readonly liveDataDisclosurePending$: Observable<boolean> =
    this.facade.liveDataDisclosurePending$;
  readonly panelScope$: Observable<CopilotPanelScope> = this.facade.panelScope$;
  readonly agentSettings$: Observable<CopilotAgentSettings> = this.facade.agentSettings$;
  readonly briefGreeting$: Observable<string | null> = this.facade.briefGreeting$;
  readonly personalityOptions = ADVISOR_PERSONA_OPTIONS;
  /** Mirrors gateway `AGENT_MULTI_PERSONA_ENABLED` — false hides advisor UI. */
  readonly multiPersonaEnabled = COPILOT_MULTI_PERSONA_ENABLED;
  agentSettings: CopilotAgentSettings = { ...DEFAULT_COPILOT_AGENT_SETTINGS };
  readonly activeClarification$: Observable<CopilotClarification | null> =
    this.facade.activeClarification$;

  /** Combined stream for template-friendly live-data + scope context. */
  readonly liveDataContext$ = combineLatest([
    this.liveData$,
    this.panelScope$,
  ]).pipe(map(([liveData, scope]) => ({ liveData, scope })));

  /** Latest Kevin brief greeting (null → static empty-state copy). */
  briefGreeting: string | null = null;

  /** i18n keys for empty-state starter prompts — route, role, and live-data aware. */
  get suggestionKeys(): readonly string[] {
    return copilotSuggestionsForContext({
      route: this.router.url,
      liveDataStatus: this.currentLiveDataStatus,
      isManager: this.store.selectSnapshot(AuthState.isManager),
    });
  }

  get suggestionItems(): ReadonlyArray<{ key: string, icon: string, label: string }> {
    return this.suggestionKeys.map((key) => {
      const meta = copilotSuggestionMeta(key);
      return {
        key,
        icon: meta.icon,
        label: this.translate.instant(key),
      };
    });
  }

  get visibleSuggestionItems(): ReadonlyArray<{ key: string, icon: string, label: string }> {
    return this.suggestionItems.slice(0, 3);
  }

  get hasRouteSpecificSuggestions(): boolean {
    return copilotHasRouteSpecificSuggestions(this.router.url);
  }

  get emptyStateHeadline(): string {
    const parts = this.briefGreetingParts();
    if (parts) {
      return parts.headline;
    }
    const firstName = this.userFirstName();
    if (firstName) {
      return this.translate.instant('copilot.emptyState.headlinePersonalized', { name: firstName });
    }
    return this.translate.instant('copilot.emptyState.headline', {
      name: this.assistantDisplayName,
    });
  }

  get emptyStateSubtext(): string {
    const parts = this.briefGreetingParts();
    if (parts) {
      return parts.subtext;
    }
    return this.translate.instant('copilot.emptyState.subtext');
  }

  get hasBriefGreeting(): boolean {
    return !!this.briefGreeting?.trim();
  }

  get showLargeEmptyBadge(): boolean {
    return !this.hasSeenEmptyState;
  }

  get composerPlaceholder(): string {
    const keys = this.composerPlaceholderKeys;
    const index = this.placeholderIndex % keys.length;
    return this.translate.instant(keys[index]);
  }

  get showFirstRunTips(): boolean {
    return this.firstRunTipsVisible;
  }

  readonly guidanceCapabilities: readonly CopilotGuidanceCapability[] =
    COPILOT_GUIDANCE_CAPABILITIES;

  /** chat | guide | usage | skills. */
  panelView: CopilotPanelView = 'chat';
  /** Expanded capability accordion section id (one at a time). */
  expandedGuidanceId: string | null = COPILOT_GUIDANCE_CAPABILITIES[0]?.id ?? null;
  /** Soft token quota snapshot for Usage settings. */
  tokenQuota: TokenQuotaUsage | null = null;
  /** Ranked top consumers for Usage (names resolved); empty when no data. */
  topUsersToday: CopilotTopUserRow[] = [];
  /** GrowthBook `copilotTokenUsageTopUsers` — reactive for OnPush. */
  topUsersFlagEnabled = false;
  usageLoading = false;
  usageError = false;
  /** Poll while Usage is open so GB / used counters update without leaving the page. */
  private static readonly usageRefreshMs = 30_000;
  private usageRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private usageVisibilityHandler: (() => void) | null = null;
  /** Session cache: userId → display name while Usage stays open. */
  private readonly topUserNameCache = new Map<string, string>();
  /** Skills settings page: catalog + entitlement badges. */
  skillsList: CopilotSkillListItem[] = [];
  panelWidth = COPILOT_PANEL_DEFAULT_WIDTH;

  draft = '';
  copiedMessageId: string | null = null;
  copiedDraftKey: string | null = null;
  firstRunTipsVisible = false;
  hasSeenEmptyState = false;
  placeholderIndex = 0;
  currentLiveDataStatus: CopilotLiveDataStatus = 'disconnected';
  private placeholderTimer: ReturnType<typeof setInterval> | null = null;
  private readonly composerPlaceholderKeys = [
    'copilot.emptyState.placeholder',
    'copilot.emptyState.placeholderHours',
    'copilot.emptyState.placeholderMention',
    'copilot.emptyState.placeholderTeam',
  ] as const;
  /** User-edited coaching draft text keyed by draftKey(messageId, index). */
  private readonly draftEdits = new Map<string, string>();
  /** Local See/Hide the trend / people / suggested approach toggles — not a new chat turn. */
  private readonly shortFormatOpen = new Map<
    string,
    { trend: boolean, people: boolean, approach: boolean }
  >();
  /** Stable TableSpec[] so *ngFor does not recreate the grid every CD cycle. */
  private readonly peopleTableCache = new Map<string, TableSpec[]>();
  private copiedResetTimer: ReturnType<typeof setTimeout> | null = null;
  private resizeMoveHandler?: (event: PointerEvent) => void;
  private resizeUpHandler?: (event: PointerEvent) => void;

  @ViewChild('mentionComposer')
  private mentionComposer?: MentionComposerComponent;

  @ViewChild('scrollContainer')
  private scrollContainer?: ElementRef<HTMLElement>;

  @ViewChild('dateFromInput')
  private dateFromInput?: ElementRef<HTMLInputElement>;

  @ViewChild('dateToInput')
  private dateToInput?: ElementRef<HTMLInputElement>;

  private lastScrollHeight = 0;
  /**
   * Force-scroll on the next layout pass (e.g. AI signal / new send). The
   * stick-to-bottom heuristic alone misses these when prior chat history left
   * the viewport away from the bottom.
   */
  private pendingPinToBottom = false;
  /**
   * See the people / See the trend remounts a table or chart in place. Stick-
   * to-bottom plus `scroll-behavior: smooth` fights that growth and bounces
   * the log. Freeze until the new height settles.
   */
  suppressStickToBottom = false;
  private scrollFreezeSawChange = false;
  private scrollFreezeTimer: ReturnType<typeof setTimeout> | null = null;
  /** Only pin to bottom when the user is already near it — otherwise hover
   * tooltips / chart CD can nudge scrollHeight and yank the panel down. */
  private static readonly stickToBottomPx = 96;

  readonly scopePresets: CopilotScopePreset[] = [
    { id: 'last7', labelKey: 'copilot.scope.last7', range: () => this.rangeForDays(7) },
    { id: 'last14', labelKey: 'copilot.scope.last14', range: () => this.rangeForDays(14) },
    { id: 'last30', labelKey: 'copilot.scope.last30', range: () => this.rangeForDays(30) },
    { id: 'thisMonth', labelKey: 'copilot.scope.thisMonth', range: () => this.thisMonthRange() },
    { id: 'lastMonth', labelKey: 'copilot.scope.lastMonth', range: () => this.lastMonthRange() },
  ];

  constructor(
    private readonly facade: CopilotFacadeService,
    private readonly agentGateway: AgentGatewayService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly clipboard: ClipboardService,
    private readonly cdr: ChangeDetectorRef,
    private readonly growthBook: GrowthBookService,
    private readonly store: Store,
    private readonly segmentService: SegmentService,
    private readonly resolve: ResolveService,
  ) {}

  /** GrowthBook gate for the top-consumers list on Usage. */
  get showTopUsersSection(): boolean {
    return this.topUsersFlagEnabled;
  }

  private static readonly sidePanelEventCategory = 'Ask AI Side Panel Events';
  private static readonly sidePanelLocation = 'Ask AI Side Panel';

  private trackSidePanelEvent(name: string): void {
    this.segmentService.trackInAppEvents(CopilotSidepanelComponent.sidePanelEventCategory, {
      name,
      location: CopilotSidepanelComponent.sidePanelLocation,
      origin: globalThis.document?.title ?? '',
    });
  }

  get mentionsEnabled(): boolean {
    // Tempo @-mentions are core to copilot — do not inherit Ask AI's opt-in flag.
    const copilot = this.growthBook.getFeatureValue<{
      mentionsAutocomplete?: boolean;
    }>(COPILOT_FEATURE_FLAG, null);
    if (copilot?.mentionsAutocomplete === false) {
      return false;
    }
    return true;
  }

  /** Display name for the user message avatar (initials). */
  get userDisplayName(): string {
    return this.store.selectSnapshot(AuthState.user)?.name?.trim() || '?';
  }

  ngOnInit(): void {
    this.panelWidth = this.loadPanelWidth();
    this.hasSeenEmptyState = this.readStorageFlag(COPILOT_EMPTY_STATE_SEEN_KEY);
    this.firstRunTipsVisible = !this.readStorageFlag(COPILOT_FIRST_RUN_TIPS_KEY);
    this.startPlaceholderRotation();

    this.growthBook
      .getFeature<boolean>(COPILOT_USAGE_TOP_USERS_FLAG, false)
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((enabled) => {
        const next = enabled === true;
        const turnedOn = next && !this.topUsersFlagEnabled;
        this.topUsersFlagEnabled = next;
        if (turnedOn && this.tokenQuota) {
          void this.resolveTopUsers(this.tokenQuota).then((rows) => {
            this.topUsersToday = rows;
            this.cdr.markForCheck();
          });
        }
        if (!next) {
          this.topUsersToday = [];
        }
        this.cdr.markForCheck();
      });

    this.liveData$
      .pipe(untilDestroyed(this))
      .subscribe((status) => {
        this.currentLiveDataStatus = status;
        this.cdr.markForCheck();
      });

    this.panelScope$
      .pipe(untilDestroyed(this))
      .subscribe((scope) => this.syncScopeInputs(scope));

    this.facade.agentSettings$
      .pipe(untilDestroyed(this))
      .subscribe((settings) => {
        this.agentSettings = settings;
        this.cdr.markForCheck();
      });

    this.briefGreeting$
      .pipe(untilDestroyed(this))
      .subscribe((greeting) => {
        this.briefGreeting = greeting;
        this.cdr.markForCheck();
      });

    this.activeClarification$
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((clarification) => {
        if (clarification === 'date_range') {
          this.contextDrawerOpen = true;
          this.cdr.markForCheck();
        }
        if (clarification === 'user' || clarification === 'team') {
          setTimeout(() => {
            const wire = this.facade.composerWireForClarification;
            if (wire) {
              this.mentionComposer?.restoreWire(wire);
            }
            this.mentionComposer?.focusForMention();
            this.cdr.markForCheck();
          }, 0);
        }
      });

    // Insight "AI signal" clicks and composer sends append at the bottom —
    // jump there even when the user was reading earlier history.
    this.isSending$
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((sending) => {
        if (sending) {
          this.pendingPinToBottom = true;
          this.pinScrollToBottom();
          this.cdr.markForCheck();
        }
      });
  }

  ngAfterViewChecked(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) {
      return;
    }
    if (this.pendingPinToBottom) {
      this.pinScrollToBottom();
      return;
    }
    if (this.suppressStickToBottom) {
      if (el.scrollHeight !== this.lastScrollHeight) {
        this.scrollFreezeSawChange = true;
        this.lastScrollHeight = el.scrollHeight;
        return;
      }
      if (this.scrollFreezeSawChange) {
        this.releaseMessageScrollFreeze();
      }
      return;
    }
    if (el.scrollHeight === this.lastScrollHeight) {
      return;
    }
    // Measure against the *previous* height so growth while pinned at the
    // bottom still sticks (streaming / new charts), without yanking when the
    // user has scrolled up to inspect a chart and hover nudges layout.
    const distanceFromBottomBeforeGrow =
      this.lastScrollHeight === 0
        ? 0
        : this.lastScrollHeight - el.scrollTop - el.clientHeight;
    const wasNearBottom =
      distanceFromBottomBeforeGrow <= CopilotSidepanelComponent.stickToBottomPx;
    this.lastScrollHeight = el.scrollHeight;
    if (wasNearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }

  /** Scroll the message log to the latest turn (AI signal / new send). */
  private pinScrollToBottom(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollTop = el.scrollHeight;
    this.lastScrollHeight = el.scrollHeight;
    this.pendingPinToBottom = false;
  }

  private freezeMessageScroll(): void {
    this.suppressStickToBottom = true;
    this.scrollFreezeSawChange = false;
    if (this.scrollFreezeTimer) {
      clearTimeout(this.scrollFreezeTimer);
    }
    this.scrollFreezeTimer = setTimeout(() => this.releaseMessageScrollFreeze(), 400);
  }

  private releaseMessageScrollFreeze(): void {
    if (this.scrollFreezeTimer) {
      clearTimeout(this.scrollFreezeTimer);
      this.scrollFreezeTimer = null;
    }
    const el = this.scrollContainer?.nativeElement;
    if (el) {
      this.lastScrollHeight = el.scrollHeight;
    }
    this.suppressStickToBottom = false;
    this.scrollFreezeSawChange = false;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.placeholderTimer) {
      clearInterval(this.placeholderTimer);
      this.placeholderTimer = null;
    }
    if (this.scrollFreezeTimer) {
      clearTimeout(this.scrollFreezeTimer);
      this.scrollFreezeTimer = null;
    }
    this.stopUsageAutoRefresh();
    this.detachResizeListeners();
  }

  onResizePointerDown(event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture(event.pointerId);

    this.resizeMoveHandler = (moveEvent: PointerEvent) => {
      this.setPanelWidthFromPointer(moveEvent.clientX);
    };
    this.resizeUpHandler = (upEvent: PointerEvent) => {
      handle.releasePointerCapture(upEvent.pointerId);
      this.detachResizeListeners();
      this.persistPanelWidth();
      this.cdr.markForCheck();
    };

    document.addEventListener('pointermove', this.resizeMoveHandler);
    document.addEventListener('pointerup', this.resizeUpHandler);
    document.addEventListener('pointercancel', this.resizeUpHandler);
  }

  /** Tracks once per conversation; reset on new chat / conversation switch. */
  private sessionInteractionTracked = false;

  onClose(): void {
    this.trackSidePanelEvent('X Actions Clicked');
    this.facade.close();
  }

  onNewChat(): void {
    this.trackSidePanelEvent('New Chat Clicked');
    this.sessionInteractionTracked = false;
    this.showChatView();
    this.facade.createConversation();
    this.cdr.markForCheck();
    setTimeout(() => this.mentionComposer?.focus(), 0);
  }

  onMoreActionsClick(): void {
    this.trackSidePanelEvent('More Actions Clicked');
  }

  onContentAndFiltersClick(): void {
    this.trackSidePanelEvent('Content & Filters Clicked');
    this.toggleContextDrawer();
  }

  onSelectConversation(id: string): void {
    this.sessionInteractionTracked = false;
    this.facade.switchConversation(id);
  }

  onCloseConversation(event: Event, id: string): void {
    event.stopPropagation();
    event.preventDefault();
    this.facade.closeConversation(id);
  }

  conversationTitle(tab: CopilotConversationTab): string {
    const title = (tab.title || '').trim();
    if (!title || title === COPILOT_UNTITLED_CONVERSATION) {
      return this.translate.instant('copilot.conversationUntitled');
    }
    return title;
  }

  trackByConversation(_index: number, tab: CopilotConversationTab): string {
    return tab.id;
  }

  onConnectLiveDataFromHint(): void {
    void this.facade.connectLiveData();
  }

  dismissFirstRunTips(): void {
    this.trackSidePanelEvent('Close Tips Clicked');
    this.firstRunTipsVisible = false;
    this.writeStorageFlag(COPILOT_FIRST_RUN_TIPS_KEY);
    this.cdr.markForCheck();
  }

  restoreFirstRunTips(): void {
    this.trackSidePanelEvent('Tip Selected');
    this.firstRunTipsVisible = true;
    this.cdr.markForCheck();
  }

  onFirstRunTipConnect(): void {
    void this.facade.connectLiveData();
  }

  onFirstRunTipMention(): void {
    this.onComposerInteraction();
    this.mentionComposer?.focusForMention();
    this.cdr.markForCheck();
  }

  /** Fires once per conversation on the first composer edit (typing, incl. `@`) — not on every keystroke. */
  onComposerInteraction(): void {
    if (this.sessionInteractionTracked) {
      return;
    }
    this.sessionInteractionTracked = true;
    this.trackSidePanelEvent('Chat Session Interacted');
  }

  markEmptyStateSeen(): void {
    if (this.hasSeenEmptyState) {
      return;
    }
    this.hasSeenEmptyState = true;
    this.writeStorageFlag(COPILOT_EMPTY_STATE_SEEN_KEY);
    this.cdr.markForCheck();
  }

  scopeContextSummary(scope: CopilotPanelScope): string | null {
    if (this.currentLiveDataStatus !== 'connected') {
      return null;
    }
    const summary = this.periodSummary(scope);
    const tz = scope.timezone?.trim() || this.viewerZone();
    if (!summary || summary === this.translate.instant('copilot.scope.selectPeriod')) {
      return null;
    }
    return this.translate.instant('copilot.scope.usingContext', { period: summary, timezone: tz });
  }

  suggestionAriaLabel(label: string): string {
    return this.translate.instant('copilot.suggestions.sendPrompt', { prompt: label });
  }

  liveDataSourceLabel(
    status: CopilotLiveDataStatus,
    tool?: string | null,
  ): string {
    if (tool === 'live_workspace_data') {
      return this.translate.instant('copilot.liveData.sourceLive');
    }
    if (tool === 'search_help_center') {
      return this.translate.instant('copilot.liveData.sourceHelp');
    }
    if (tool) {
      return this.translate.instant('copilot.liveData.sourceReports');
    }
    return status === 'connected'
      ? this.translate.instant('copilot.liveData.sourceReports')
      : this.translate.instant('copilot.liveData.sourceHelp');
  }

  isFirstAssistantMessage(message: CopilotUiMessage, messages: CopilotUiMessage[]): boolean {
    if (message.role !== 'assistant') {
      return false;
    }
    return messages.find((m) => m.role === 'assistant' && !m.pending)?.id === message.id;
  }

  showGuideView(): void {
    this.stopUsageAutoRefresh();
    this.panelView = 'guide';
    this.closeContextDrawer();
    this.cdr.markForCheck();
  }

  showUsageView(): void {
    this.panelView = 'usage';
    this.closeContextDrawer();
    this.usageError = false;
    this.usageLoading = true;
    this.tokenQuota = null;
    this.topUsersToday = [];
    this.cdr.markForCheck();
    void this.loadTokenQuota();
    this.startUsageAutoRefresh();
  }

  showSkillsView(): void {
    this.stopUsageAutoRefresh();
    this.panelView = 'skills';
    this.closeContextDrawer();
    this.refreshSkillsList();
    this.cdr.markForCheck();
  }

  skillsAvailableCount(): number {
    return this.skillsList.filter((s) => s.entitled).length;
  }

  onTrySkillPrompt(skill: CopilotSkillListItem): void {
    this.showChatView();
    void this.facade.send(skill.examplePrompt);
  }

  onUnlockSkill(skill: CopilotSkillListItem): void {
    void this.router.navigate([skill.billingRoute]);
  }

  showChatView(): void {
    this.stopUsageAutoRefresh();
    this.panelView = 'chat';
    this.cdr.markForCheck();
  }

  private refreshSkillsList(): void {
    const entitled = resolveCopilotAgentSkills(
      this.growthBook.getFeatureValue(
        COPILOT_AGENT_SKILLS_FLAG,
        [...COPILOT_PHASE1_AGENT_SKILLS],
      ),
    );
    this.skillsList = buildCopilotSkillsList(entitled);
  }

  formatTokenCount(n: number): string {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) {
      return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
    }
    if (abs >= 1_000) {
      return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
    }
    return String(Math.round(n));
  }

  quotaBarWidth(percentUsed: number): string {
    const pct = Math.min(100, Math.max(0, percentUsed * 100));
    return `${pct}%`;
  }

  quotaTone(percentUsed: number): 'ok' | 'warn' | 'over' {
    if (percentUsed >= 1) return 'over';
    if (percentUsed >= 0.8) return 'warn';
    return 'ok';
  }

  private startUsageAutoRefresh(): void {
    this.stopUsageAutoRefresh();
    this.usageRefreshTimer = setInterval(() => {
      if (this.panelView !== 'usage') {
        this.stopUsageAutoRefresh();
        return;
      }
      void this.loadTokenQuota({ silent: true });
    }, CopilotSidepanelComponent.usageRefreshMs);

    this.usageVisibilityHandler = () => {
      if (document.visibilityState === 'visible' && this.panelView === 'usage') {
        void this.loadTokenQuota({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', this.usageVisibilityHandler);
  }

  private stopUsageAutoRefresh(): void {
    if (this.usageRefreshTimer) {
      clearInterval(this.usageRefreshTimer);
      this.usageRefreshTimer = null;
    }
    if (this.usageVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.usageVisibilityHandler);
      this.usageVisibilityHandler = null;
    }
  }

  private async loadTokenQuota(options?: { silent?: boolean }): Promise<void> {
    const silent = options?.silent === true;
    if (!silent) {
      this.usageLoading = true;
      this.cdr.markForCheck();
    }
    try {
      const pricingPlan = this.store.selectSnapshot(AuthState.company)?.pricingPlan;
      const snap = await this.agentGateway.getTokenQuotaUsage(pricingPlan);
      this.tokenQuota = snap;
      this.usageError = snap == null;
      // Always resolve attribution when present; GB only gates the section UI.
      this.topUsersToday = snap ? await this.resolveTopUsers(snap) : [];
    } catch {
      if (!silent) {
        this.tokenQuota = null;
        this.topUsersToday = [];
      }
      this.usageError = true;
    } finally {
      if (!silent) {
        this.usageLoading = false;
      }
      this.cdr.markForCheck();
    }
  }

  /**
   * Map gateway topUsersToday → display rows with names.
   * Prefer gateway-provided name, then ManagerState roster (incl. archived), then
   * ResolveService batch lookup (name || email), else a friendly unknown label.
   * Showing truncated userIds as the primary label was the STAFF-145677 bug.
   */
  private async resolveTopUsers(snap: TokenQuotaUsage): Promise<CopilotTopUserRow[]> {
    const rows = snap.topUsersToday ?? [];
    if (!rows.length) {
      return [];
    }

    for (const row of rows) {
      const fromGateway = row.name?.trim();
      if (row.userId && fromGateway) {
        this.topUserNameCache.set(row.userId, fromGateway);
      }
    }

    const self = this.store.selectSnapshot(AuthState.user);
    const selfLabel = this.displayLabelFromUser(self);
    if (self?.id && selfLabel) {
      this.topUserNameCache.set(self.id, selfLabel);
    }

    const managed =
      this.store.selectSnapshot(ManagerState.users.includeArchived(true)) ?? [];
    for (const u of managed) {
      const label = this.displayLabelFromUser(u);
      if (u?.id && label) {
        this.topUserNameCache.set(u.id, label);
      }
    }

    const missing = [...new Set(
      rows.map((r) => r.userId).filter((id) => id && !this.topUserNameCache.has(id)),
    )];

    if (missing.length) {
      try {
        const resolved = await this.resolve.resolveUsers(missing, {
          detail: 'alias',
          'include-archived-users': 1,
        });
        for (const id of missing) {
          const label = this.displayLabelFromUser(resolved.get(id));
          if (label) {
            this.topUserNameCache.set(id, label);
          }
        }
      } catch {
        // Soft-fail: keep friendly unknown labels for unresolved users.
      }
    }

    return rows.map((r, index) => ({
      userId: r.userId,
      name: this.topUserNameCache.get(r.userId) ?? this.fallbackUserLabel(r.userId),
      tokens: r.tokens,
      rank: index + 1,
    }));
  }

  /** Prefer human-readable identity: name, then email (same as @mention labels). */
  private displayLabelFromUser(
    user: { name?: string, email?: string } | null | undefined,
  ): string {
    return user?.name?.trim() || user?.email?.trim() || '';
  }

  private fallbackUserLabel(userId: string): string {
    return this.translate.instant('copilot.usage.topUsersUnknown', {
      id: (userId?.trim() || '?').slice(0, 8),
    });
  }

  toggleGuidanceSection(id: string): void {
    this.expandedGuidanceId = this.expandedGuidanceId === id ? null : id;
    this.cdr.markForCheck();
  }

  isGuidanceExpanded(id: string): boolean {
    return this.expandedGuidanceId === id;
  }

  onGuidancePrompt(promptKey: string): void {
    this.showChatView();
    void this.facade.send(this.translate.instant(promptKey));
  }

  onToggleLiveData(status: CopilotLiveDataStatus | null): void {
    if (status === 'connected') {
      this.facade.disconnectLiveData();
    } else if (status === 'disconnected' || status === null) {
      void this.facade.connectLiveData();
    }
  }

  onSubmit(): void {
    this.mentionComposer?.onSend();
  }

  onComposerSubmit(payload: MentionComposerSubmit): void {
    this.trackSidePanelEvent('Send Prompt Clicked');
    this.markEmptyStateSeen();
    void this.facade.send(payload.displayText, {
      wire: payload.wire,
      displayText: payload.displayText,
      gatewayMessage: payload.gatewayMessage,
      selection: payload.selection,
    });
  }

  onMentionNeedsConfirm(event: MentionComposerClarification): void {
    this.facade.requestMentionConfirmation({
      reason: event.reason,
      label: event.label,
      wire: event.wire,
      clarification: event.clarification,
    });
    this.cdr.markForCheck();
  }

  onDateFromChange(event: Event): void {
    const from = this.clampDate((event.target as HTMLInputElement).value);
    const to = this.clampDate(this.dateToValue || from);
    this.applyDateRange(from, to >= from ? to : from);
  }

  onDateToChange(event: Event): void {
    const to = this.clampDate((event.target as HTMLInputElement).value);
    const from = this.clampDate(this.dateFromValue || to);
    this.applyDateRange(from <= to ? from : to, to);
  }

  get dateFromDisplayValue(): string {
    return this.formatDateForDisplay(this.dateFromValue);
  }

  get dateToDisplayValue(): string {
    return this.formatDateForDisplay(this.dateToValue);
  }

  private formatDateForDisplay(iso: string): string {
    if (!iso) return '';
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.toFormat('dd/MM/yyyy') : iso;
  }

  openFromPicker(): void {
    const el = this.dateFromInput?.nativeElement;
    if (!el || el.disabled) return;
    try { el.showPicker(); } catch { el.focus(); }
  }

  openToPicker(): void {
    const el = this.dateToInput?.nativeElement;
    if (!el || el.disabled) return;
    try { el.showPicker(); } catch { el.focus(); }
  }

  onDateFromTextBlur(event: FocusEvent): void {
    const value = (event.target as HTMLInputElement).value.trim();
    const parsed = this.parseDateFlexible(value);
    if (parsed) {
      const to = this.clampDate(this.dateToValue || parsed);
      this.applyDateRange(parsed, to >= parsed ? to : parsed);
    }
  }

  onDateToTextBlur(event: FocusEvent): void {
    const value = (event.target as HTMLInputElement).value.trim();
    const parsed = this.parseDateFlexible(value);
    if (parsed) {
      const from = this.clampDate(this.dateFromValue || parsed);
      this.applyDateRange(from <= parsed ? from : parsed, parsed);
    }
  }

  private parseDateFlexible(value: string): string | null {
    if (!value) return null;
    const formats = [
      'yyyy-MM-dd', 'dd/MM/yyyy', 'MM/dd/yyyy', 'd/M/yyyy', 'M/d/yyyy',
      'dd-MM-yyyy', 'MM-dd-yyyy', 'dd.MM.yyyy',
      'MMMM d, yyyy', 'MMMM d yyyy', 'd MMMM yyyy',
      'MMM d, yyyy', 'd MMM yyyy', 'MMM d yyyy',
    ];
    for (const fmt of formats) {
      const dt = DateTime.fromFormat(value, fmt);
      if (dt.isValid) return dt.toISODate() ?? null;
    }
    const iso = DateTime.fromISO(value);
    return iso.isValid ? iso.toISODate() ?? null : null;
  }

  onScopePresetClick(preset: CopilotScopePreset): void {
    if (!this.contextDrawerOpen) {
      this.snapshotContext();
      this.contextDrawerOpen = true;
    }
    const { from, to } = preset.range();
    this.applyDateRange(from, to, true);
  }

  get maxSelectableDate(): string {
    return DateTime.now().setZone(this.viewerZone()).toISODate() ?? '';
  }

  get activePresetId(): string | null {
    const from = this.dateFromValue;
    const to = this.dateToValue;
    if (!from || !to) {
      return null;
    }
    const match = this.scopePresets.find((preset) => {
      const range = preset.range();
      return range.from === from && range.to === to;
    });
    return match?.id ?? null;
  }

  syncScopeInputs(scope: CopilotPanelScope): void {
    if (!scope.dateRange?.from || !scope.dateRange?.to) {
      this.cdr.markForCheck();
      return;
    }
    // Keep panelScope + inputs on inclusive YYYY-MM-DD. Display used to clamp
    // future exclusive ends without rewriting scope — Ask AI then queried a
    // different window than the picker showed (STAFF-145650).
    const normalized = normalizeInclusivePanelDateRange(
      scope.dateRange.from,
      scope.dateRange.to,
      {
        timeZone: scope.timezone || this.viewerZone(),
        today: this.maxSelectableDate,
      },
    );
    if (!normalized) {
      this.cdr.markForCheck();
      return;
    }
    const scopeFrom = scope.dateRange.from.slice(0, 10);
    const scopeTo = scope.dateRange.to.slice(0, 10);
    const needsRewrite =
      scope.dateRange.from.includes('T') ||
      scope.dateRange.to.includes('T') ||
      scopeFrom !== normalized.from ||
      scopeTo !== normalized.to;
    if (needsRewrite) {
      this.facade.setPanelDateRange(normalized.from, normalized.to, false);
      return;
    }
    this.dateFromValue = normalized.from;
    this.dateToValue = normalized.to;
    this.cdr.markForCheck();
  }

  scopeBarHighlighted(clarification: CopilotClarification | null): boolean {
    return clarification === 'date_range';
  }

  toggleContextDrawer(): void {
    if (!this.contextDrawerOpen) {
      this.snapshotContext();
    }
    this.contextDrawerOpen = !this.contextDrawerOpen;
    this.cdr.markForCheck();
  }

  openContextDrawer(): void {
    if (this.contextDrawerOpen) {
      return;
    }
    this.snapshotContext();
    this.contextDrawerOpen = true;
    this.cdr.markForCheck();
  }

  closeContextDrawer(): void {
    if (!this.contextDrawerOpen) {
      return;
    }
    this.contextDrawerOpen = false;
    this.contextSnapshot = null;
    this.cdr.markForCheck();
  }

  onContextDrawerSave(): void {
    this.contextSnapshot = null;
    this.contextDrawerOpen = false;
    this.cdr.markForCheck();
  }

  onContextDrawerCancel(): void {
    if (this.contextSnapshot) {
      const s = this.contextSnapshot;
      this.dateFromValue = s.dateFrom;
      this.dateToValue = s.dateTo;
      this.facade.setPanelDateRange(s.dateFrom, s.dateTo);
      this.agentSettings = { ...this.agentSettings, ...s.agentSettings };
      this.facade.setAdvisorPersona(s.agentSettings.advisorPersona);
      this.facade.setDecisionEngineEnabled(s.agentSettings.decisionEngineEnabled);
      this.contextSnapshot = null;
    }
    this.contextDrawerOpen = false;
    this.cdr.markForCheck();
  }

  get hasContextChanges(): boolean {
    const s = this.contextSnapshot;
    if (!s) return false;
    const advisorChanged =
      this.multiPersonaEnabled &&
      this.agentSettings.advisorPersona !== s.agentSettings.advisorPersona;
    return (
      this.dateFromValue !== s.dateFrom ||
      this.dateToValue !== s.dateTo ||
      advisorChanged ||
      this.agentSettings.decisionEngineEnabled !== s.agentSettings.decisionEngineEnabled
    );
  }

  private contextSnapshot: {
    dateFrom: string;
    dateTo: string;
    agentSettings: Pick<CopilotAgentSettings, 'advisorPersona' | 'decisionEngineEnabled'>;
  } | null = null;

  private snapshotContext(): void {
    this.contextSnapshot = {
      dateFrom: this.dateFromValue,
      dateTo: this.dateToValue,
      agentSettings: {
        advisorPersona: this.agentSettings.advisorPersona,
        decisionEngineEnabled: this.agentSettings.decisionEngineEnabled,
      },
    };
  }

  /** @deprecated Prefer {@link toggleContextDrawer}; kept for call-site clarity. */
  toggleScopePanel(): void {
    this.toggleContextDrawer();
  }

  /** @deprecated Prefer {@link closeContextDrawer}. */
  closeScopePanel(): void {
    this.closeContextDrawer();
  }

  /** @deprecated Prefer {@link toggleContextDrawer}. */
  toggleIntelligencePanel(): void {
    this.toggleContextDrawer();
  }

  /** @deprecated Prefer {@link closeContextDrawer}. */
  closeIntelligencePanel(): void {
    this.closeContextDrawer();
  }

  /** @deprecated Prefer {@link toggleContextDrawer}. */
  toggleContextStrip(): void {
    this.toggleContextDrawer();
  }

  personalitySummary(): string {
    if (!this.multiPersonaEnabled) {
      return '';
    }
    const match = this.personalityOptions.find((o) => o.id === this.agentSettings.advisorPersona);
    return match ? this.translate.instant(match.labelKey) : '';
  }

  contextChipSummary(scope: CopilotPanelScope, liveData: CopilotLiveDataStatus): string {
    const parts: string[] = [];
    const personality = this.personalitySummary();
    if (personality) {
      parts.push(personality);
    }
    if (liveData === 'connected' || liveData === 'connecting') {
      parts.push(this.periodSummary(scope));
    }
    return parts.join(' · ');
  }

  timezoneShort(scope: CopilotPanelScope): string {
    const tz = (scope.timezone?.trim() || this.viewerZone()).trim();
    if (!tz) {
      return '';
    }
    const slash = tz.lastIndexOf('/');
    const label = slash >= 0 ? tz.slice(slash + 1) : tz;
    return label.replace(/_/g, ' ');
  }

  onPersonalityChange(persona: AdvisorPersonaId): void {
    this.facade.setAdvisorPersona(persona);
    this.cdr.markForCheck();
  }

  periodSummary(scope: CopilotPanelScope): string {
    const from = scope.dateRange?.from?.slice(0, 10) || this.dateFromValue;
    const to = scope.dateRange?.to?.slice(0, 10) || this.dateToValue;
    if (!from || !to) {
      return this.translate.instant('copilot.scope.selectPeriod');
    }
    const zone = this.viewerZone();
    const start = DateTime.fromISO(from, { zone });
    const end = DateTime.fromISO(to, { zone });
    if (!start.isValid || !end.isValid) {
      return `${from} – ${to}`;
    }
    if (start.year === end.year) {
      return `${start.toFormat('d MMM')} – ${end.toFormat('d MMM yyyy')}`;
    }
    return `${start.toFormat('d MMM yyyy')} – ${end.toFormat('d MMM yyyy')}`;
  }

  dateFromValue = '';
  dateToValue = '';
  contextDrawerOpen = false;

  /** Alias for legacy templates/tests — maps to the unified context drawer. */
  get scopePanelOpen(): boolean {
    return this.contextDrawerOpen;
  }
  set scopePanelOpen(open: boolean) {
    this.contextDrawerOpen = open;
  }

  /** Alias for legacy templates/tests — maps to the unified context drawer. */
  get intelligencePanelOpen(): boolean {
    return this.contextDrawerOpen;
  }
  set intelligencePanelOpen(open: boolean) {
    this.contextDrawerOpen = open;
  }

  private applyDateRange(from: string, to: string, openPicker = false): void {
    const clampedFrom = this.clampDate(from);
    let clampedTo = this.clampDate(to);
    if (clampedTo < clampedFrom) {
      clampedTo = clampedFrom;
    }
    this.dateFromValue = clampedFrom;
    this.dateToValue = clampedTo;
    this.facade.setPanelDateRange(clampedFrom, clampedTo);
    this.cdr.markForCheck();
    if (openPicker) {
      setTimeout(() => this.openDatePicker(), 0);
    }
  }

  private openDatePicker(): void {
    const el = this.dateFromInput?.nativeElement;
    if (!el || el.disabled) {
      return;
    }
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        // Safari / older browsers may throw when not user-gestured.
      }
    }
    el.focus();
    el.click();
  }

  private viewerZone(): string {
    return (
      this.store.selectSnapshot(AuthState.userTimezone) ??
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  }

  private rangeForDays(days: number): { from: string, to: string } {
    const end = DateTime.now().setZone(this.viewerZone()).startOf('day');
    const start = end.minus({ days: days - 1 });
    return {
      from: start.toISODate() ?? '',
      to: end.toISODate() ?? '',
    };
  }

  /** Match gateway DATE_RANGE_SUGGESTED_ACTIONS presets (calendar semantics). */
  private rangeForDateClarificationPreset(
    preset: CopilotDateClarificationPreset | undefined,
  ): { from: string, to: string } | null {
    if (!preset) {
      return null;
    }
    const zone = this.viewerZone();
    const today = DateTime.now().setZone(zone).startOf('day');
    if (preset === 'today') {
      const day = today.toISODate() ?? '';
      return { from: day, to: day };
    }
    if (preset === 'this_month') {
      return this.thisMonthRange();
    }
    if (preset === 'last_week') {
      // Previous Mon–Sun week (same as gateway inferDateRangeFromMessage).
      const thisMonday = today.minus({ days: (today.weekday + 6) % 7 });
      const end = thisMonday.minus({ days: 1 });
      const start = end.minus({ days: 6 });
      return {
        from: start.toISODate() ?? '',
        to: end.toISODate() ?? '',
      };
    }
    return null;
  }

  private thisMonthRange(): { from: string, to: string } {
    const end = DateTime.now().setZone(this.viewerZone()).startOf('day');
    const start = end.startOf('month');
    return {
      from: start.toISODate() ?? '',
      to: end.toISODate() ?? '',
    };
  }

  private lastMonthRange(): { from: string, to: string } {
    const anchor = DateTime.now().setZone(this.viewerZone()).minus({ months: 1 });
    const start = anchor.startOf('month');
    const end = anchor.endOf('month');
    return {
      from: start.toISODate() ?? '',
      to: end.toISODate() ?? '',
    };
  }

  private clampDate(value: string): string {
    if (!value) {
      return value;
    }
    const iso = value.slice(0, 10);
    const max = this.maxSelectableDate;
    return iso > max ? max : iso;
  }

  onSuggestion(key: string): void {
    this.trackSidePanelEvent('Questions for the page Clicked');
    this.markEmptyStateSeen();
    void this.facade.send(this.translate.instant(key));
  }

  onActionClick(action: CopilotAction, message: CopilotUiMessage): void {
    this.facade.onSuggestedActionClicked(action, message);
    if (action.type === 'open_scope') {
      this.openContextDrawer();
      return;
    }
    if (action.type === 'retry_last') {
      void this.facade.resendLast();
      return;
    }
    if (action.type === 'apply_date_preset') {
      const range = this.rangeForDateClarificationPreset(
        action['preset'] as CopilotDateClarificationPreset | undefined,
      );
      if (range) {
        // setPanelDateRange auto-resends the pending question when clarification
        // is date_range — keeps person/question context instead of sending the
        // chip label ("Last week") as a new standalone prompt.
        this.applyDateRange(range.from, range.to);
      }
      return;
    }
    if (action.route) {
      if (action.params && Object.keys(action.params).length) {
        const params = Object.fromEntries(
          Object.entries(action.params).filter(
            ([key, value]) => !!key?.trim() && value != null && String(value).trim() !== '',
          ),
        );
        void this.router.navigate(
          Object.keys(params).length ? [action.route, params] : [action.route],
        );
      } else {
        void this.router.navigate([action.route]);
      }
      return;
    }
    if (action.prompt) {
      void this.facade.send(action.prompt);
    }
  }

  answerSegments(message: CopilotUiMessage): ReportAnswerSegment[] {
    return splitReportLinkPhrases(message.content, message.reportLinks);
  }

  openReportLink(link: ReportLink): void {
    const params = link.params
      ? Object.fromEntries(
          Object.entries(link.params).filter(
            ([key, value]) => !!key?.trim() && value != null && String(value).trim() !== '',
          ),
        )
      : {};
    void this.router.navigate(
      Object.keys(params).length ? [link.route, params] : [link.route],
    );
  }

  unusedReportLinks(message: CopilotUiMessage): ReportLink[] {
    const used = new Set(
      this.answerSegments(message)
        .filter((s): s is ReportAnswerSegment & { kind: 'link' } => s.kind === 'link')
        .map((s) => s.link.id),
    );
    return (message.reportLinks ?? []).filter((l) => !used.has(l.id));
  }

  shortFormatVisualToggles(
    message: CopilotUiMessage,
  ): { trend: boolean, people: boolean, approach: boolean } | null {
    return shortFormatToggleFlags(message);
  }

  peopleTables(message: CopilotUiMessage): TableSpec[] {
    if (message.tables?.length) {
      return message.tables;
    }
    const hit = this.peopleTableCache.get(message.id);
    if (hit) {
      return hit;
    }
    const tables = peopleTablesForMessage(message);
    this.peopleTableCache.set(message.id, tables);
    return tables;
  }

  tableTrackBy(_index: number, table: TableSpec): string {
    return table.id;
  }

  isShortFormatTrendOpen(messageId: string): boolean {
    return this.shortFormatOpen.get(messageId)?.trend === true;
  }

  isShortFormatPeopleOpen(messageId: string): boolean {
    return this.shortFormatOpen.get(messageId)?.people === true;
  }

  isShortFormatApproachOpen(messageId: string): boolean {
    return this.shortFormatOpen.get(messageId)?.approach === true;
  }

  showLiveChart(message: CopilotUiMessage): boolean {
    if (!message.charts?.length) {
      return false;
    }
    if (isSinglePersonCard(message)) {
      return true;
    }
    return this.isShortFormatTrendOpen(message.id);
  }

  showLiveTable(message: CopilotUiMessage): boolean {
    if (isSinglePersonCard(message)) {
      return !!message.tables?.length;
    }
    if (!this.isShortFormatPeopleOpen(message.id)) {
      return false;
    }
    return this.peopleTables(message).length > 0;
  }

  showCoachingCard(message: CopilotUiMessage): boolean {
    if (message.decision || message.recommendationCard || !message.coachingSuggestions?.length) {
      return false;
    }
    if (message.shortFormat) {
      return this.isShortFormatApproachOpen(message.id);
    }
    return true;
  }

  toggleShortFormatTrend(messageId: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.freezeMessageScroll();
    const open = !this.isShortFormatTrendOpen(messageId);
    this.shortFormatOpen.set(messageId, { trend: open, people: false, approach: false });
    this.cdr.markForCheck();
  }

  toggleShortFormatPeople(messageId: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.freezeMessageScroll();
    const open = !this.isShortFormatPeopleOpen(messageId);
    this.shortFormatOpen.set(messageId, { trend: false, people: open, approach: false });
    this.cdr.markForCheck();
  }

  toggleShortFormatApproach(messageId: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.freezeMessageScroll();
    const open = !this.isShortFormatApproachOpen(messageId);
    this.shortFormatOpen.set(messageId, { trend: false, people: false, approach: open });
    this.cdr.markForCheck();
  }

  onHelpLinkClick(link: HelpLink): void {
    this.facade.onHelpLinkClicked(link);
    // External Help Center article — open in a new tab; `noopener` prevents the
    // opened page from accessing our window (security).
    window.open(link.url, '_blank', 'noopener');
  }

  showMessageActions(message: CopilotUiMessage): boolean {
    return !message.pending && !message.transient && message.content.trim().length > 0;
  }

  showMessageFeedback(message: CopilotUiMessage): boolean {
    return (
      this.showMessageActions(message) &&
      message.role === 'assistant' &&
      !!message.requestId
    );
  }

  async onFeedback(message: CopilotUiMessage, rating: 'helpful' | 'not_helpful'): Promise<void> {
    // Allow changing mind (thumbs up → down); ignore duplicate clicks on the same rating.
    if (message.feedbackRating === rating) {
      return;
    }
    await this.facade.submitMessageFeedback(message, rating);
    this.cdr.markForCheck();
  }

  async onCopyMessage(message: CopilotUiMessage): Promise<void> {
    await this.copyText(message.content.trim(), message.id);
  }

  async onCopyDraft(coaching: CoachingSuggestion, messageId: string, index: number): Promise<void> {
    const key = this.draftKey(messageId, index);
    await this.copyText(this.draftText(coaching, messageId, index).trim(), key);
  }

  draftText(coaching: CoachingSuggestion, messageId: string, index: number): string {
    const key = this.draftKey(messageId, index);
    return this.draftEdits.get(key) ?? coaching.draftMessage;
  }

  onDraftInput(coaching: CoachingSuggestion, messageId: string, index: number, value: string): void {
    this.draftEdits.set(this.draftKey(messageId, index), value);
  }

  recommendationDraftText(card: RecommendationCard, messageId: string): string {
    return this.draftEdits.get(this.recommendationDraftKey(messageId)) ?? card.draftMessage;
  }

  recommendationDraftLabel(card: RecommendationCard): string {
    return resolveRecommendationDraftLabel({
      draftMessage: card.draftMessage,
      draftLabel: card.draftLabel,
      body: card.body,
      fallbackLabel: this.translate.instant('copilot.coaching.draftLabel'),
      exampleLabel: this.translate.instant('copilot.coaching.exampleLabel'),
    });
  }

  onRecommendationDraftInput(messageId: string, value: string): void {
    this.draftEdits.set(this.recommendationDraftKey(messageId), value);
  }

  recommendationDraftKey(messageId: string): string {
    return `${messageId}-recommendation-draft`;
  }

  /**
   * Client-only recommendation intents. Send note copies the draft (no gateway write);
   * Skip dismisses the card; Ask for a call inserts a follow-up prompt.
   */
  async onRecommendationAction(
    action: RecommendationAction,
    card: RecommendationCard,
    message: CopilotUiMessage,
  ): Promise<void> {
    const draft = this.recommendationDraftText(card, message.id).trim();
    if (action.kind === 'client_send_note') {
      await this.copyText(draft, this.recommendationDraftKey(message.id));
      return;
    }
    if (action.kind === 'client_skip') {
      this.facade.clearRecommendationCard(message.id);
      this.cdr.markForCheck();
      return;
    }
    if (action.kind === 'client_ask_call') {
      void this.facade.send(
        this.translate.instant('copilot.recommendation.askCallPrompt'),
      );
    }
  }

  onConfirmLiveDataDisclosure(): void {
    void this.facade.confirmLiveDataDisclosure();
  }

  onDeclineLiveDataDisclosure(): void {
    this.facade.declineLiveDataDisclosure();
  }

  draftCopyTooltip(messageId: string, index: number): string {
    const key = this.draftKey(messageId, index);
    return this.translate.instant(
      this.copiedDraftKey === key ? 'copilot.coaching.copied' : 'copilot.coaching.copy',
    );
  }

  draftKey(messageId: string, index: number): string {
    return `${messageId}-draft-${index}`;
  }

  private async copyText(text: string, key: string): Promise<void> {
    const { success } = await this.clipboard.copyToClipboard(text);
    if (!success) {
      return;
    }
    if (this.copiedResetTimer) {
      clearTimeout(this.copiedResetTimer);
    }
    if (key.includes('-draft-')) {
      this.copiedDraftKey = key;
      this.copiedMessageId = null;
    } else {
      this.copiedMessageId = key;
      this.copiedDraftKey = null;
    }
    this.cdr.markForCheck();
    this.copiedResetTimer = setTimeout(() => {
      this.copiedMessageId = null;
      this.copiedDraftKey = null;
      this.cdr.markForCheck();
    }, 2000);
  }

  onRetryMessage(message: CopilotUiMessage): void {
    void this.facade.retryMessage(message.id);
  }

  copyTooltip(message: CopilotUiMessage): string {
    return this.translate.instant(
      this.copiedMessageId === message.id ? 'copilot.message.copied' : 'copilot.message.copy',
    );
  }

  /** Human-readable breakdown for the token-usage tooltip ("812 in · 143 out · gpt-4o-mini"). */
  tokenTooltip(message: CopilotUiMessage): string {
    const usage = message.usage;
    if (!usage) {
      return '';
    }
    const parts = [`${usage.promptTokens} in`, `${usage.completionTokens} out`];
    if (message.model) {
      parts.push(message.model);
    }
    return parts.join(' · ');
  }

  trackByMessage(_index: number, message: CopilotUiMessage): string {
    return message.id;
  }

  toolStepTrackBy(_index: number, step: ToolStep): string {
    return step.tool;
  }

  toolStepMark(step: ToolStep, pending?: boolean): string {
    return step.done || !pending ? '✓' : '·';
  }

  hasAssistantText(message: CopilotUiMessage): boolean {
    return !!message.content?.trim();
  }

  userMessageSegments(content: string | undefined | null, wire?: string): UserMessageDisplayPart[] {
    return userMessageSegments(wire ?? content);
  }

  userMessagePartTrackBy(index: number): number {
    return index;
  }

  clarificationHint(message: CopilotUiMessage): string | null {
    if (!message.clarification) {
      return null;
    }
    const key = `copilot.clarification.${message.clarification}`;
    const translated = this.translate.instant(key);
    return translated === key ? null : translated;
  }

  /**
   * User-facing notes derived from gateway caveats. Known machine codes map to
   * translations; free-text caveats (already full sentences) render verbatim.
   */
  caveatNotes(message: CopilotUiMessage): string[] {
    if (message.pending || message.role !== 'assistant' || !message.caveats?.length) {
      return [];
    }
    const notes: string[] = [];
    for (const caveat of message.caveats) {
      if (caveat === 'result_truncated') {
        notes.push(this.translate.instant('copilot.caveats.truncated'));
      } else if (caveat === 'insight_watch_proposed') {
        notes.push(this.translate.instant('copilot.caveats.insightWatchProposed'));
      } else if (caveat === 'insight_watch_created') {
        notes.push(this.translate.instant('copilot.caveats.insightWatchCreated'));
      } else if (caveat.includes(' ')) {
        // Sentence-shaped caveats from the gateway (e.g. unverified figures).
        notes.push(caveat);
      }
    }
    return notes;
  }

  private userFirstName(): string | null {
    const name = this.store.selectSnapshot(AuthState.user)?.name?.trim();
    if (!name) {
      return null;
    }
    return name.split(/\s+/)[0] ?? null;
  }

  /**
   * Split a multi-paragraph Kevin brief: first paragraph → headline,
   * remaining paragraphs → subtext (joined). Single paragraph → headline only.
   */
  private briefGreetingParts(): { headline: string, subtext: string } | null {
    const raw = this.briefGreeting?.trim();
    if (!raw) {
      return null;
    }
    const paragraphs = raw.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
    if (!paragraphs.length) {
      return null;
    }
    return {
      headline: paragraphs[0],
      subtext: paragraphs.slice(1).join('\n\n'),
    };
  }

  private startPlaceholderRotation(): void {
    this.placeholderTimer = setInterval(() => {
      this.placeholderIndex = (this.placeholderIndex + 1) % this.composerPlaceholderKeys.length;
      this.cdr.markForCheck();
    }, 6000);
  }

  private readStorageFlag(key: string): boolean {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  private writeStorageFlag(key: string): void {
    try {
      localStorage.setItem(key, '1');
    } catch {
      // Ignore storage failures.
    }
  }

  private maxPanelWidth(): number {
    return Math.min(COPILOT_PANEL_MAX_WIDTH, Math.floor(window.innerWidth * 0.92));
  }

  private clampPanelWidth(width: number): number {
    return Math.min(this.maxPanelWidth(), Math.max(COPILOT_PANEL_MIN_WIDTH, Math.round(width)));
  }

  private loadPanelWidth(): number {
    try {
      const raw = localStorage.getItem(COPILOT_PANEL_WIDTH_STORAGE_KEY);
      const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
      if (!Number.isFinite(parsed)) {
        return COPILOT_PANEL_DEFAULT_WIDTH;
      }
      return this.clampPanelWidth(parsed);
    } catch {
      return COPILOT_PANEL_DEFAULT_WIDTH;
    }
  }

  private persistPanelWidth(): void {
    try {
      localStorage.setItem(COPILOT_PANEL_WIDTH_STORAGE_KEY, String(this.panelWidth));
    } catch {
      // Ignore storage failures.
    }
  }

  private setPanelWidthFromPointer(clientX: number): void {
    this.panelWidth = this.clampPanelWidth(window.innerWidth - clientX);
    this.cdr.markForCheck();
  }

  private detachResizeListeners(): void {
    if (this.resizeMoveHandler) {
      document.removeEventListener('pointermove', this.resizeMoveHandler);
      this.resizeMoveHandler = undefined;
    }
    if (this.resizeUpHandler) {
      document.removeEventListener('pointerup', this.resizeUpHandler);
      document.removeEventListener('pointercancel', this.resizeUpHandler);
      this.resizeUpHandler = undefined;
    }
  }
}
